<script lang="ts" setup>
import { computed } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { OrganizationsDocument, OrganizationType } from '@/gql/graphql';
import { BFormGroup, BFormSelect } from 'bootstrap-vue-next';
import { ORGANIZATION_USER_ROLES } from '@/constants';

// --- Component properties ---
const props = defineProps<{
  label: string | null;
  allowNullOrganization: boolean;
  includeOwnerFullName: boolean;
  includeOwnerEmail: boolean;
  includeCountryCode: boolean;
  modelValue: OrganizationType | null;
}>();

const emit = defineEmits(['update:modelValue']);

// --- Query Organizations ---
const { result: resultFetchData, onResult: onFetchDataResult } = useQuery(
  OrganizationsDocument,
  {
    pagingItemsPerPage: 0, // 0 to fetch all organizations without paging
  }
);

onFetchDataResult((res) => {
  console.debug('Organizations fetched:', res.data);

  if (props.modelValue) {
    const matching = options.value.find(
      (opt) =>
        opt.value && props.modelValue && opt.value.id === props.modelValue.id
    );
    if (matching) {
      selectedOrganization.value = matching.value;
    }
  }
});

const organizations = computed(() => {
  const orgs = resultFetchData.value?.organizations?.organizations || [];
  return orgs
    .filter((org) => org && org !== null)
    .map((org) => wellTypedOrganization(org));
});

function wellTypedOrganization(org: OrganizationType | null): OrganizationType {
  return {
    ...org,
    createdAt: org?.createdAt ? new Date(org.createdAt) : null,
    updatedAt: org?.updatedAt ? new Date(org.updatedAt) : null,
    usersAssociated: org?.usersAssociated
      ?.filter((assoc) => assoc && assoc !== null)
      .map((assoc) => ({
        ...assoc,
        createdAt: assoc?.createdAt ? new Date(assoc.createdAt) : null,
        updatedAt: assoc?.updatedAt ? new Date(assoc.updatedAt) : null,
        removedAt: assoc?.removedAt ? new Date(assoc.removedAt) : null,
      })),
  };
}

// Helper method to build the display text for an organization option
function buildOrganizationText(org: OrganizationType): string {
  let additionalTextItems: string[] = [];
  // (Assume these fields exist on the organization and its associated owner.)
  // For instance:
  if (org.usersAssociated) {
    const owner = org.usersAssociated.find(
      (a) => a && a.userRole === ORGANIZATION_USER_ROLES.OWNER
    );
    if (owner && owner.user?.email) {
      additionalTextItems.push(owner.user.email);
    }
  }
  if (org.addressCountryCode) {
    additionalTextItems.push(org.addressCountryCode.toUpperCase());
  }
  const additionalText =
    additionalTextItems.length > 0
      ? ` (${additionalTextItems.join(', ')})`
      : '';
  return `${org.name}${additionalText}`;
}

// Build the options. If defaultValue is provided and not present,
// add it to the beginning of the list.
const options = computed(() => {
  let orgOptions: { text: string; value: OrganizationType | null }[] =
    organizations.value.map((org) => ({
      text: buildOrganizationText(org),
      value: org,
    }));
  if (props.modelValue) {
    const defaultExists = orgOptions.some(
      (opt) =>
        opt.value && props.modelValue && opt.value.id === props.modelValue.id
    );
    if (!defaultExists) {
      // Prepend the default organization option if not present.
      console.debug('Default organization not found in options, adding it.');
      orgOptions.unshift({
        text: buildOrganizationText(props.modelValue),
        value: wellTypedOrganization(props.modelValue),
      });
    } else console.debug('Default organization already exists in options');
  }
  // Add a null option at the beginning if requested.
  if (props.allowNullOrganization) {
    orgOptions.unshift({ text: 'None', value: null });
  }
  return orgOptions;
});

// Use a computed getter/setter for the v-model binding.
const selectedOrganization = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit('update:modelValue', val);
  },
});
</script>
<template>
  <BFormGroup label-for="org-picker-select">
    <template #label v-if="props.label">
      {{ props.label }}
    </template>
    <BFormSelect
      id="org-picker-select"
      name="org-picker-select"
      v-model="selectedOrganization"
      :options="options"
      required
    />
  </BFormGroup>
</template>
